import { useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { AuditDataProvider, useAuditCase } from './AuditDataSource';
import { auditSource } from './sources/auditSource';
import { CaseDocumentPane } from './primitives/caseDocumentPane';

// The popped-out document window (HUB-504). Opened by the compare screen's "Pop
// out" action at /workbench/audit-doc?case=<id>. It is NOT a raw PDF tab — it
// renders the SAME styled Coronis document viewer (navy record header + PDF.js
// canvas + footer), so the auditor gets the product chrome in a second window
// (typically a second monitor) while the review fields take the full main window.
// "Return to review" (or just closing the window) hands control back: the opener
// polls window.closed and restores the in-view pane.
//
// The theme (light / off-white / dark) is applied by the app-root ThemeProvider,
// so the window matches the main workbench automatically. Self-contained data:
// its own QueryClient + the audit source, so it resolves the case (and with it
// the case's own document blob id) independently of the opener.
//
// It shares CaseDocumentPane with the in-view pane so both windows can only ever
// show the SAME document for a case — including the "no document" state. When
// this window rendered its own copy of the viewer it drifted into showing the
// bundled synthetic sample unconditionally, which is exactly the failure an
// audit tool cannot have.
//
// PHI hygiene: the URL carries only the blind case id (e.g. AC-100482) — never
// the MRN, name, or any payload field. Display fields are looked up client-side.
function DocWindowInner() {
  const [params] = useSearchParams();
  const caseId = params.get('case') ?? '';
  const { data } = useAuditCase(caseId);
  const mrn = data?.mrn ?? '';
  const procedure = data?.record.procedure ?? '';

  return (
    <div className="flex h-screen flex-col bg-background text-foreground">
      <CaseDocumentPane
        className="flex min-h-0 flex-1 flex-col"
        // Empty until the case resolves, which renders the "no document" state
        // briefly. That is the honest reading: we do not yet know of a
        // document, and the alternative — assuming one — is how the sample got
        // shown for real cases in the first place.
        documentBlobId={data?.documentBlobId ?? ''}
        mrn={mrn}
        procedure={procedure}
        footerSuffix="Close this window to return to the review"
        headerAction={
          <button
            type="button"
            onClick={() => window.close()}
            title="Close this window and return to the review"
            className="inline-flex items-center gap-1.5 rounded-coronis bg-white/10 px-2.5 py-1.5 text-[12px] font-medium text-white transition-colors hover:bg-white/20"
          >
            <ArrowLeft className="size-3.5" />
            Return to review
          </button>
        }
      />
    </div>
  );
}

export function AuditDocWindow() {
  const [client] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          // Retries, MATCHING the app's own client (main.tsx). This was
          // retry: false, which made the window strictly less able to recover
          // than the tab that opened it — a transient failure here was
          // terminal, and its only visible form is "no document for this
          // case", indistinguishable from a case that genuinely has none.
          //
          // The auth race that made that reachable is fixed at the route
          // (ProtectedRoute now holds the render until the session resolves),
          // so this is defence in depth rather than the fix. It stays because
          // the asymmetry is the trap: two clients for one product, with
          // different resilience, and only one of them exercised in dev.
          queries: { retry: 3, staleTime: 30 * 1000 },
        },
      }),
  );
  return (
    <QueryClientProvider client={client}>
      <AuditDataProvider source={auditSource}>
        <DocWindowInner />
      </AuditDataProvider>
    </QueryClientProvider>
  );
}
